import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { adminAPI } from '../services/api';
import TeamMemberForm from '../components/forms/TeamMemberForm';

const TeamMemberFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    prefix: '',
    name: '',
    position: '',
    department: '',
    email: '',
    phone: '',
    bio: '',
    imageUrl: '',
    imageFile: null,
    order: 0,
    isLeadership: false,
    isActive: true
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [originalImageUrl, setOriginalImageUrl] = useState(''); // ← Track original image

  // Load team member data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const loadTeamMember = async () => {
        try {
          const member = await adminAPI.getTeamMemberById(id);
          
          const imageUrl = member.image || '';
          
          setFormData({
            prefix: member.prefix || '',
            name: member.name || '',
            position: member.role || '',
            department: member.department || '',
            email: member.email || '',
            phone: member.phone || '',
            bio: member.bio || '',
            imageUrl: imageUrl,
            imageFile: null,
            order: member.order || 0,
            isLeadership: !!member.is_leadership,
            isActive: member.is_active !== false
          });
          
          setOriginalImageUrl(imageUrl); // ← Store original image URL
        } catch (error) {
          console.error('Error loading team member:', error);
          toast.error('Failed to load team member data');
          navigate('/admin/dashboard');
        }
      };
      loadTeamMember();
    }
  }, [id, isEditMode, navigate]);

  const validateForm = () => {
    const requiredFields = ['prefix', 'name', 'position', 'bio'];
    const missingFields = requiredFields.filter(field => !formData[field]?.trim());
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return false;
    }
    
    if (formData.bio && formData.bio.length < 10) {
      toast.error('Bio must be at least 10 characters long');
      return false;
    }
    
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Determine if image was removed
      const imageWasRemoved = isEditMode && originalImageUrl && !formData.imageUrl && !formData.imageFile;
      
      // Always use FormData for team members
      const formDataToSend = new FormData();
      
      // Add file if exists
      if (formData.imageFile) {
        formDataToSend.append('file', formData.imageFile);
      }
      
      // Add remove_image flag if image was removed
      if (imageWasRemoved) {
        formDataToSend.append('remove_image', 'true');
      }
      
      // Add all other fields
      formDataToSend.append('prefix', formData.prefix || '');
      formDataToSend.append('name', formData.name || '');
      formDataToSend.append('role', formData.position || '');
      formDataToSend.append('bio', formData.bio || '');
      
      formDataToSend.append('email', formData.email || '');
      formDataToSend.append('phone', formData.phone || '');
      formDataToSend.append('department', formData.department || '');
      
      formDataToSend.append('order', formData.order || 0);
      formDataToSend.append('is_leadership', formData.isLeadership ? 'true' : 'false');
      formDataToSend.append('is_active', formData.isActive ? 'true' : 'false');
      
      if (isEditMode) {
        await adminAPI.updateTeamMember(id, formDataToSend);
        toast.success('Team member updated successfully');
      } else {
        await adminAPI.createTeamMember(formDataToSend);
        toast.success('Team member added successfully');
      }
      
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error saving team member:', error);
      
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to save team member';
      
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  return (
    <div className="container mx-auto p-6">
      <TeamMemberForm
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitting={submitting}
        isEdit={isEditMode}
      />
    </div>
  );
};

export default TeamMemberFormPage;